import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Grid,
  Dialog,
  DialogContent,
  Avatar,
  Pagination,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { getPhotosAndAlbums } from '../../../../Utils/apiService';
import routes from '../../../../Utils/routes';
import KenLoader from '../../../../GlobalComponents/KenLoader';
import CollaboratorsModal from '../AlbumContainer/CollaboratorsModal';

const ITEMS_PER_PAGE = 20;

interface FileRecord {
  Id: string;
  Name: string;
  File_Link__c: string;
  CreatedDate: string;
  Description__c?: string;
  Location__c?: string;
  File_Admin__r?: {
    Id?: string;
    Name?: string;
    Profile_Picture_URL__c?: string;
  };
  Alumni_Photo_Tags__r?: {
    records: any[];
  };
}

interface FolderData {
  Id: string;
  Name: string;
  Folder_Type__c: string;
  Folder_Access__c: string;
  CreatedDate: string;
  LastModifiedDate: string;
  Files__r?: {
    totalSize: number;
    records: FileRecord[];
  };
}

const AlbumViewer: React.FC = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true); // album fetch loader
  const [gridLoading, setGridLoading] = useState(true); // grid/dialog loader (like PhotoContainer)
  const [albumData, setAlbumData] = useState<FolderData | null>(null);
  const [images, setImages] = useState<FileRecord[]>([]);

  // States to mimic PhotoContainer behavior
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedData, setPaginatedData] = useState<FileRecord[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FileRecord | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [collaboratorsModalOpen, setCollaboratorsModalOpen] = useState(false);

  const [photosData, setPhotosData] = useState<any[]>([]);
  const [albumsData, setAlbumsData] = useState<any[]>([]);

  // Helper: get URL param
  const getUrlParameter = (name: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  };

  // Helper: update URL with photoid
  const updateUrl = (photoId: string | null) => {
    const url = new URL(window.location.href);
    if (photoId) {
      url.searchParams.set('photoid', photoId);
    } else {
      url.searchParams.delete('photoid');
    }
    window.history.replaceState({}, '', url.toString());
  };

  useEffect(() => {
    console.log('Folder ID from URL params:', folderId);
  }, [folderId]);

  // Resize listener (like PhotoContainer)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch album data
  useEffect(() => {
    const fetchAlbumData = async () => {
      try {
        setLoading(true);
        const response = await getPhotosAndAlbums();
        console.log('API Response:', response);

        let foundAlbum: FolderData | null = null;
        if (response && response.success) {
          console.log('All albums:', response.data.data.albums);
          foundAlbum = response.data.data.albums.find(
            (album: FolderData) => album.Id === folderId
          );
          console.log('Found album:', foundAlbum);
        }

        if (foundAlbum) {
          setAlbumData(foundAlbum);

          if (foundAlbum.Files__r && foundAlbum.Files__r.records) {
            console.log('All files in album:', foundAlbum.Files__r.records);

            const validImages = foundAlbum.Files__r.records.filter(
              (file: FileRecord) =>
                file.File_Link__c &&
                file.File_Link__c.startsWith('http') &&
                !file.File_Link__c.includes('Insert failed')
            );

            setImages(validImages);
          } else {
            setImages([]);
          }
        } else {
          console.error('Album not found with ID:', folderId);
          setImages([]);
        }
      } catch (error) {
        console.error('Error fetching album data:', error);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    if (folderId) {
      fetchAlbumData();
    }
  }, [folderId]);

  // Fetch all photos and albums for upload navigation
  useEffect(() => {
    const fetchAllPhotosAndAlbums = async () => {
      try {
        const response = await getPhotosAndAlbums();
        if (response && response.data && response.data.data) {
          setPhotosData(response.data.data.photos || []);
          setAlbumsData(response.data.data.albums || []);
        }
      } catch (error) {
        console.error('Error fetching all photos and albums:', error);
      }
    };

    fetchAllPhotosAndAlbums();
  }, []);

  // Pagination + URL photoid handling (like PhotoContainer)
  useEffect(() => {
    setGridLoading(true);

    const timer = setTimeout(() => {
      if (images?.length) {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const sliced = images.slice(startIndex, endIndex);
        setPaginatedData(sliced);
        setTotalPages(Math.ceil(images.length / ITEMS_PER_PAGE));

        const photoId = getUrlParameter('photoid');
        if (photoId && !openDialog) {
          const photoIndex = images.findIndex((item) => item.Id === photoId);
          if (photoIndex !== -1) {
            const photo = images[photoIndex];
            const photoPage = Math.ceil((photoIndex + 1) / ITEMS_PER_PAGE);

            if (photoPage !== currentPage) {
              setCurrentPage(photoPage);
              setGridLoading(false);
              return; // next effect run will open dialog
            }

            const paginatedIndex = sliced.findIndex((item) => item.Id === photoId);
            if (paginatedIndex !== -1) {
              handleOpenDialog(photo, paginatedIndex);
            }
          }
        }
      } else {
        setPaginatedData([]);
        setTotalPages(1);
      }

      setGridLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [images, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handleAddPhotos = () => {
    const albumsInfo = albumsData.map((album: any) => ({
      id: album.Id,
      name: album.Name,
    }));

    const photosInfo = photosData.map((photo: any) => ({
      id: photo.Id,
      name: photo.Name,
      fileLink: photo.File_Link__c,
      description: photo.Description__c,
      location: photo.Location__c,
      createdDate: photo.CreatedDate,
    }));

    navigate(routes.PhotoAlbumUpload, {
      state: {
        albums: albumsInfo,
        photos: photosInfo,
        currentAlbumId: folderId,
        selectedAlbum: folderId,
      },
    });
  };

  const isValidImageUrl = (url: string | null | undefined) => {
    return url && url.trim() !== '';
  };

  const handleOpenDialog = (item: FileRecord, index: number) => {
    setSelectedItem(item);
    setSelectedIndex(index);
    setOpenDialog(true);
    setImageLoading(false);
    updateUrl(item.Id);
  };

  const handleNextImage = () => {
    setImageLoading(true);
    const nextIndex = (selectedIndex + 1) % paginatedData.length;
    setSelectedIndex(nextIndex);
    setSelectedItem(paginatedData[nextIndex]);
    updateUrl(paginatedData[nextIndex].Id);
  };

  const handlePrevImage = () => {
    setImageLoading(true);
    const prevIndex =
      selectedIndex === 0 ? paginatedData.length - 1 : selectedIndex - 1;
    setSelectedIndex(prevIndex);
    setSelectedItem(paginatedData[prevIndex]);
    updateUrl(paginatedData[prevIndex].Id);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    updateUrl(null);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          height: '83.5vh',
          backgroundColor: '#fff',
          borderRadius: '9px',
          width: '100%',
        }}
      >
        <KenLoader />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        borderRadius: '9px',
        height: '83.5vh',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '@media (min-width: 1280px) and (max-width: 1300px)': {
          height: '81vh',
        },
        '@media (max-width: 1600px)': {
          height: '83.5vh',
        },
        '@media (max-width: 1360px)': {
          height: '100vh',
        },
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" p={3} pb={2}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ color: '#123A6F', mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h5"
          fontWeight={500}
          color="#123A6F"
          sx={{ flex: 1 }}
        >
          {albumData?.Name || 'Album'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPhotos}
          sx={{
            backgroundColor: '#123A6F',
            '&:hover': { backgroundColor: '#0A2851' },
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            py: 1,
            fontSize: '14px',
            fontWeight: 500,
            ml: 2,
          }}
        >
          Add more photos
        </Button>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 3,
          pt: 0,
        }}
        className="scrollbar-cus"
      >
        {images.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            sx={{
              height: 'calc(100% - 20px)',
              textAlign: 'center',
              padding: 3,
            }}
          >
            <ImageIcon
              sx={{ fontSize: 80, color: '#123A6F', opacity: 0.6, mb: 2 }}
            />
            <Typography variant="h4" color="#373A45" mb={1}>
              No images available
            </Typography>
            <Typography variant="body2" color="#6F7787" mb={3}>
              Start by uploading your first image
            </Typography>
          </Box>
        ) : gridLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="400px"
          >
            <KenLoader />
          </Box>
        ) : (
          <Paper
            sx={{
              borderRadius: 3,
              padding: { xs: 1, sm: 2 },
              boxShadow: 'none',
              backgroundColor: 'transparent',
              width: '100%',
            }}
          >
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {paginatedData.map((item, index) => (
                <Grid item xs={6} sm={6} md={4} lg={3} key={item.Id}>
                  <Paper
                    elevation={1}
                    sx={{
                      borderRadius: { xs: 2, sm: 3 },
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                      position: 'relative',
                      height: { xs: 150, sm: 180, md: 220 },
                    }}
                    onClick={() => handleOpenDialog(item, index)}
                  >
                    {isValidImageUrl(item.File_Link__c) ? (
                      <Box
                        component="img"
                        src={item.File_Link__c}
                        alt={item.Name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f0f0f0',
                        }}
                      >
                        <ImageIcon
                          sx={{
                            fontSize: { xs: 40, sm: 60, md: 80 },
                            color: '#bdbdbd',
                          }}
                        />
                      </Box>
                    )}

                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      display="flex"
                      alignItems="center"
                      p={{ xs: 1, sm: 2 }}
                      width="100%"
                      sx={{
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                      }}
                    >
                      <Avatar
                        src={item.File_Admin__r?.Profile_Picture_URL__c}
                        alt={item.Name}
                        sx={{
                          width: { xs: 24, sm: 30, md: 36 },
                          height: { xs: 24, sm: 30, md: 36 },
                          marginRight: 1,
                        }}
                      />
                      <Typography
                        fontWeight={500}
                        color="white"
                        fontSize={{ xs: 12, sm: 13, md: 14 }}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.File_Admin__r?.Name}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={{ xs: 2, sm: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  variant="outlined"
                  shape="rounded"
                  siblingCount={isMobile ? 0 : 1}
                  boundaryCount={isMobile ? 0 : 1}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '8px',
                      fontWeight: 400,
                      color: '#000',
                      border: '1px solid #E0E0E0',
                      '&:hover': {
                        backgroundColor: '#F5F5F5',
                      },
                    },
                    '& .MuiPaginationItem-page.Mui-selected': {
                      backgroundColor: '#123A6F',
                      color: '#fff',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: '#123A6F',
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Dialog preview – same structure as PhotoContainer */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
            width: '800px',
            height: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            margin: { xs: '8px', sm: '16px', md: '24px' },
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="flex-end"
          p={1}
          position="absolute"
          right={0}
          zIndex={3}
          sx={{ backgroundColor: 'rgba(255,255,255,0.8)' }}
        >
          <IconButton
            onClick={handleCloseDialog}
            sx={{ color: '#000', fontSize: '12px' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {paginatedData.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                backgroundColor: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>

            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                backgroundColor: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </>
        )}

        {selectedItem && (
          <DialogContent
            sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flexShrink: 0, p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography
                  variant="h6"
                  fontSize={{ xs: 16, sm: 18 }}
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  {selectedItem.Name}
                </Typography>

                {selectedItem.Description__c && (
                  <Typography
                    variant="body2"
                    fontSize={{ xs: 12, sm: 14 }}
                    fontWeight={500}
                    color="text.secondary"
                  >
                    {selectedItem.Description__c}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {imageLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      zIndex: 2,
                    }}
                  >
                    <KenLoader />
                  </Box>
                )}
                {isValidImageUrl(selectedItem.File_Link__c) ? (
                  <Box
                    component="img"
                    src={selectedItem.File_Link__c}
                    alt={selectedItem.Name}
                    onLoad={() => setImageLoading(false)}
                    onError={() => setImageLoading(false)}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      opacity: imageLoading ? 0 : 1,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0f0f0',
                      opacity: imageLoading ? 0 : 1,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                    onLoad={() => setImageLoading(false)}
                  >
                    <ImageIcon
                      sx={{
                        fontSize: { xs: 60, sm: 100, md: 150 },
                        color: '#bdbdbd',
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  p: 2,
                  gap: { xs: 1, sm: 0 },
                  backgroundColor: '#f5f5f5',
                  borderTop: '1px solid #e0e0e0',
                }}
              >
                {/* User Info */}
                <Box display="flex" alignItems="center">
                  <Avatar
                    src={selectedItem.File_Admin__r?.Profile_Picture_URL__c}
                    alt={selectedItem.Name}
                    sx={{
                      width: { xs: 30, sm: 36 },
                      height: { xs: 30, sm: 36 },
                      marginRight: 1,
                    }}
                  />
                  <Typography
                    fontWeight={500}
                    color="#373A45"
                    fontSize={{ xs: 12, sm: 14 }}
                  >
                    {selectedItem.Name}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  {selectedItem.Location__c && (
                    <Box display="flex" alignItems="center">
                      <LocationOnIcon
                        sx={{ fontSize: { xs: 16, sm: 18 }, color: '#666' }}
                      />
                      <Typography
                        variant="body2"
                        fontSize={{ xs: 12, sm: 14 }}
                        fontWeight={500}
                        color="text.secondary"
                      >
                        {selectedItem.Location__c}
                      </Typography>
                    </Box>
                  )}
                  {selectedItem.Alumni_Photo_Tags__r &&
                    selectedItem.Alumni_Photo_Tags__r.records &&
                    selectedItem.Alumni_Photo_Tags__r.records.length > 0 && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setCollaboratorsModalOpen(true)}
                      >
                        <LocalOfferIcon
                          sx={{ fontSize: { xs: 18, sm: 20 } }}
                        />
                        <Typography
                          fontSize={{ xs: 12, sm: 14 }}
                          fontWeight={500}
                          color="#FFD600"
                          sx={{
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px',
                          }}
                        >
                          {selectedItem.Alumni_Photo_Tags__r.records.length}
                        </Typography>
                      </Box>
                    )}
                </Box>
              </Box>
            </Box>

            <CollaboratorsModal
              open={collaboratorsModalOpen}
              onClose={() => setCollaboratorsModalOpen(false)}
              collaborators={
                selectedItem.Alumni_Photo_Tags__r &&
                selectedItem.Alumni_Photo_Tags__r.records
                  ? selectedItem.Alumni_Photo_Tags__r.records.map((tag: any) => ({
                      Id: tag.Id,
                      Contact__r: {
                        Name: tag.Alumni__r?.Name || '',
                        Profile_Picture_URL__c:
                          tag.Alumni__r?.Profile_Picture_URL__c || '',
                        Batch__c: tag.Alumni__r?.Batch__c || '',
                      },
                    }))
                  : []
              }
            />
          </DialogContent>
        )}
      </Dialog>
    </Paper>
  );
};

export default AlbumViewer;
